/**
 * The dashboard store. Three reducers, one of them RTK Query's.
 *
 * No cart here — the dashboard does not sell. Otherwise it is the POS's store with one slice
 * removed, which is the point: the same session, the same permissions, the same revoke handling.
 */

import { configureStore } from '@reduxjs/toolkit';

import {
  api,
  configureApi,
  readHint,
  resetApiState,
  restored,
  revoked,
  sessionReducer,
  themeReducer,
  wipeLocalState,
} from '@pos/shared';

import { env } from '@/shared/config/env';

export const store = configureStore({
  reducer: {
    session: sessionReducer,
    theme: themeReducer,
    [api.reducerPath]: api.reducer,
  },
  middleware: (getDefault) => getDefault().concat(api.middleware),
  devTools: !env.isProduction,
});

configureApi({
  baseUrl: env.apiBaseUrl,
  channel: 'dashboard',
  // The server already says exactly what happened (revoked by an owner, another device signed
  // in, the PIN limit was hit, or the session simply expired) without pretending to know which
  // one it was when it does not — a frontend-invented guess like "someone signed in elsewhere"
  // would be a specific, possibly false claim standing in for an honest, general one.
  onSessionEnded: (_code, message) => {
    store.dispatch(revoked(message));

    store.dispatch(resetApiState());
    void wipeLocalState();
  },
});

// Synchronous, for the same reason as the POS: the router's first render must already know which
// screen to show. A hint from a previous sign-in means a refresh cookie is presumed present, so
// the boot outcome is `paused` — the dashboard's one-click "Continue as {name}?" — rather than
// silently landing on `/sign-in` with no explanation and no attempt made at all.
store.dispatch(restored({ status: readHint() !== null ? 'paused' : 'anonymous' }));

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
